import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2, Play, Save } from "lucide-react";
import { api, toastError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

const TZ_LIST = [
  "UTC", "Europe/London", "Europe/Rome", "Europe/Madrid", "Europe/Berlin",
  "Europe/Paris", "America/New_York", "America/Chicago", "America/Denver",
  "America/Los_Angeles", "America/Sao_Paulo", "Asia/Dubai", "Asia/Kolkata",
  "Asia/Singapore", "Asia/Tokyo", "Australia/Sydney",
];
const RETRY_OPTIONS = ["busy", "no_answer", "failed", "voicemail"];
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const blank = {
  name: "",
  description: "",
  contact_list_id: "",
  agent_id: "",
  trunk_id: "",
  caller_id_did_id: "__none__",
  schedule: {
    timezone: "UTC",
    start_at: "",
    end_at: "",
    daily_window: null,
    days_of_week: null,
  },
  concurrency_cap: 10,
  pacing_per_minute: "",
  retry_rules: { max_attempts: 3, retry_on: ["busy", "no_answer", "failed"], retry_delay_seconds: 600 },
  voicemail_action: "hangup",
  voicemail_message: "",
};

function isoToInput(v) {
  if (!v) return "";
  // 2026-02-04T03:00:00+00:00 -> 2026-02-04T03:00 (datetime-local)
  return v.slice(0, 16);
}
function inputToIso(v) {
  if (!v) return null;
  // Treat as UTC for storage
  return v.length === 16 ? `${v}:00+00:00` : v;
}

export default function CampaignDesigner() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [model, setModel] = useState(blank);
  const [campaign, setCampaign] = useState(null);
  const [lists, setLists] = useState([]);
  const [agents, setAgents] = useState([]);
  const [trunks, setTrunks] = useState([]);
  const [dids, setDids] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("basics");

  const isLive = campaign && ["scheduled", "running", "paused"].includes(campaign.status);

  useEffect(() => {
    (async () => {
      try {
        const [l, a, t, d] = await Promise.all([
          api.get("/contact-lists"),
          api.get("/agents"),
          api.get("/trunks"),
          api.get("/did-numbers"),
        ]);
        setLists(l.data);
        setAgents(a.data);
        setTrunks(t.data);
        setDids(d.data);
        if (id) {
          const r = await api.get(`/campaigns/${id}`);
          const c = r.data;
          setCampaign(c);
          setModel({
            ...blank,
            ...c,
            caller_id_did_id: c.caller_id_did_id || "__none__",
            pacing_per_minute: c.pacing_per_minute ?? "",
            voicemail_message: c.voicemail_message || "",
            schedule: {
              ...blank.schedule,
              ...c.schedule,
              start_at: isoToInput(c.schedule?.start_at),
              end_at: isoToInput(c.schedule?.end_at),
            },
          });
        }
      } catch (err) {
        toastError(err, "Failed to load");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const setField = (k, v) => setModel((m) => ({ ...m, [k]: v }));
  const setSched = (k, v) => setModel((m) => ({ ...m, schedule: { ...m.schedule, [k]: v } }));
  const setRetry = (k, v) => setModel((m) => ({ ...m, retry_rules: { ...m.retry_rules, [k]: v } }));

  const filteredDids = useMemo(
    () =>
      dids.filter(
        (d) => d.use_for_outbound && d.trunk_id === model.trunk_id && d.enabled
      ),
    [dids, model.trunk_id]
  );

  const buildPayload = () => {
    const sched = {
      timezone: model.schedule.timezone || "UTC",
      start_at: inputToIso(model.schedule.start_at),
      end_at: inputToIso(model.schedule.end_at),
      daily_window: model.schedule.daily_window,
      days_of_week: model.schedule.days_of_week,
    };
    return {
      name: model.name,
      description: model.description || null,
      contact_list_id: model.contact_list_id,
      agent_id: model.agent_id,
      trunk_id: model.trunk_id,
      caller_id_did_id:
        model.caller_id_did_id && model.caller_id_did_id !== "__none__"
          ? model.caller_id_did_id
          : null,
      schedule: sched,
      concurrency_cap: Number(model.concurrency_cap),
      pacing_per_minute: model.pacing_per_minute === "" ? null : Number(model.pacing_per_minute),
      retry_rules: {
        max_attempts: Number(model.retry_rules.max_attempts),
        retry_on: model.retry_rules.retry_on,
        retry_delay_seconds: Number(model.retry_rules.retry_delay_seconds),
      },
      voicemail_action: model.voicemail_action,
      voicemail_message: model.voicemail_message || null,
    };
  };

  const valid =
    model.name.trim() &&
    model.contact_list_id &&
    model.agent_id &&
    model.trunk_id;

  const save = async (alsoLaunch = false) => {
    if (!valid) {
      toast.error("Please fill name, contact list, agent and trunk");
      return;
    }
    setSubmitting(true);
    try {
      const payload = buildPayload();
      let saved;
      if (isEdit) {
        const r = await api.put(`/campaigns/${id}`, payload);
        saved = r.data;
      } else {
        const r = await api.post("/campaigns", payload);
        saved = r.data;
      }
      if (alsoLaunch && saved.status === "draft") {
        await api.post(`/campaigns/${saved.id}/launch`);
        toast.success("Launched");
      } else {
        toast.success(isEdit ? "Saved" : "Created");
      }
      navigate(`/campaigns/${saved.id}/control`);
    } catch (err) {
      toastError(err, "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }

  const dailyWindowOn = !!model.schedule.daily_window;
  const dowOn = Array.isArray(model.schedule.days_of_week);

  return (
    <div className="space-y-6 pb-24" data-testid="campaign-designer">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {isEdit ? "Edit campaign" : "New campaign"}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isEdit
              ? "Targeting fields are locked while running. Pacing, retry & voicemail settings are always editable."
              : "Configure targeting, schedule, pacing and retry rules. Save first, then launch."}
          </p>
        </div>
        {campaign && (
          <Badge variant="outline" className="text-xs uppercase" data-testid="campaign-status-pill">
            {campaign.status}
          </Badge>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList data-testid="designer-tabs">
          <TabsTrigger value="basics" data-testid="tab-basics">Basics</TabsTrigger>
          <TabsTrigger value="targeting" data-testid="tab-targeting">Targeting</TabsTrigger>
          <TabsTrigger value="schedule" data-testid="tab-schedule">Schedule</TabsTrigger>
          <TabsTrigger value="pacing" data-testid="tab-pacing">Pacing & Retry</TabsTrigger>
        </TabsList>

        <TabsContent value="basics" className="mt-6 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={model.name}
              onChange={(e) => setField("name", e.target.value)}
              data-testid="designer-name"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="desc">Description (optional)</Label>
            <Textarea
              id="desc"
              rows={3}
              value={model.description || ""}
              onChange={(e) => setField("description", e.target.value)}
              data-testid="designer-description"
            />
          </div>
        </TabsContent>

        <TabsContent value="targeting" className="mt-6 space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Contact list</Label>
              <Select
                value={model.contact_list_id}
                onValueChange={(v) => setField("contact_list_id", v)}
                disabled={isLive}
              >
                <SelectTrigger data-testid="designer-list">
                  <SelectValue placeholder="Pick a list" />
                </SelectTrigger>
                <SelectContent>
                  {lists.map((l) => (
                    <SelectItem key={l.id} value={l.id}>
                      {l.name} <span className="text-xs text-muted-foreground">· {l.valid_rows} valid</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Agent</Label>
              <Select
                value={model.agent_id}
                onValueChange={(v) => setField("agent_id", v)}
                disabled={isLive}
              >
                <SelectTrigger data-testid="designer-agent">
                  <SelectValue placeholder="Pick an agent" />
                </SelectTrigger>
                <SelectContent>
                  {agents.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.name} <span className="text-xs text-muted-foreground">· {a.language}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Trunk</Label>
              <Select
                value={model.trunk_id}
                onValueChange={(v) => {
                  setField("trunk_id", v);
                  setField("caller_id_did_id", "__none__");
                }}
                disabled={isLive}
              >
                <SelectTrigger data-testid="designer-trunk">
                  <SelectValue placeholder="Pick a trunk" />
                </SelectTrigger>
                <SelectContent>
                  {trunks.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Caller ID (outbound DID)</Label>
              <Select
                value={model.caller_id_did_id}
                onValueChange={(v) => setField("caller_id_did_id", v)}
                disabled={isLive || !model.trunk_id}
              >
                <SelectTrigger data-testid="designer-caller-id">
                  <SelectValue placeholder="None — let the worker decide" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None — let the worker decide</SelectItem>
                  {filteredDids.map((d) => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.number} {d.label ? `· ${d.label}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!model.trunk_id && (
                <p className="text-xs text-muted-foreground">Pick a trunk to see eligible DIDs.</p>
              )}
            </div>
          </div>
          <div className="rounded-md border border-border p-4 space-y-3">
            <Label>Voicemail behaviour</Label>
            <Select
              value={model.voicemail_action}
              onValueChange={(v) => setField("voicemail_action", v)}
            >
              <SelectTrigger className="max-w-xs" data-testid="designer-vm-action"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="hangup">Hang up immediately</SelectItem>
                <SelectItem value="leave_message">Leave message</SelectItem>
              </SelectContent>
            </Select>
            {model.voicemail_action === "leave_message" && (
              <Textarea
                rows={3}
                placeholder="Hello, this is …"
                value={model.voicemail_message || ""}
                onChange={(e) => setField("voicemail_message", e.target.value)}
                data-testid="designer-vm-message"
              />
            )}
          </div>
        </TabsContent>

        <TabsContent value="schedule" className="mt-6 space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Timezone (IANA)</Label>
              <Select
                value={model.schedule.timezone}
                onValueChange={(v) => setSched("timezone", v)}
                disabled={isLive}
              >
                <SelectTrigger data-testid="designer-tz"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TZ_LIST.map((tz) => (
                    <SelectItem key={tz} value={tz}>{tz}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div />
            <div className="space-y-1.5">
              <Label htmlFor="sa">Start at (UTC)</Label>
              <Input
                id="sa"
                type="datetime-local"
                value={model.schedule.start_at}
                onChange={(e) => setSched("start_at", e.target.value)}
                disabled={isLive}
                data-testid="designer-start-at"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ea">End at (UTC)</Label>
              <Input
                id="ea"
                type="datetime-local"
                value={model.schedule.end_at}
                onChange={(e) => setSched("end_at", e.target.value)}
                data-testid="designer-end-at"
              />
            </div>
          </div>

          <div className="rounded-md border border-border p-4 space-y-3">
            <div className="flex items-center justify-between">
              <Label>Daily dialing window</Label>
              <Switch
                checked={dailyWindowOn}
                disabled={isLive}
                onCheckedChange={(v) =>
                  setSched("daily_window", v ? { start_hm: "09:00", end_hm: "17:00" } : null)
                }
                data-testid="designer-daily-window-toggle"
              />
            </div>
            {dailyWindowOn && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>From</Label>
                  <Input
                    type="time"
                    value={model.schedule.daily_window.start_hm}
                    onChange={(e) =>
                      setSched("daily_window", { ...model.schedule.daily_window, start_hm: e.target.value })
                    }
                    disabled={isLive}
                    data-testid="designer-daily-window-start"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>To</Label>
                  <Input
                    type="time"
                    value={model.schedule.daily_window.end_hm}
                    onChange={(e) =>
                      setSched("daily_window", { ...model.schedule.daily_window, end_hm: e.target.value })
                    }
                    disabled={isLive}
                    data-testid="designer-daily-window-end"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="rounded-md border border-border p-4 space-y-3">
            <div className="flex items-center justify-between">
              <Label>Days of week</Label>
              <Switch
                checked={dowOn}
                disabled={isLive}
                onCheckedChange={(v) => setSched("days_of_week", v ? [0, 1, 2, 3, 4] : null)}
                data-testid="designer-days-toggle"
              />
            </div>
            {dowOn && (
              <div className="flex flex-wrap gap-3">
                {DAYS.map((d, idx) => {
                  const checked = (model.schedule.days_of_week || []).includes(idx);
                  return (
                    <label key={d} className="flex items-center gap-2 text-sm">
                      <Checkbox
                        checked={checked}
                        disabled={isLive}
                        onCheckedChange={(v) => {
                          const cur = new Set(model.schedule.days_of_week || []);
                          if (v) cur.add(idx); else cur.delete(idx);
                          setSched("days_of_week", Array.from(cur).sort());
                        }}
                        data-testid={`designer-day-${idx}`}
                      />
                      {d}
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="pacing" className="mt-6 space-y-6">
          <div className="rounded-md border border-border p-4 space-y-4">
            <div className="flex items-center justify-between">
              <Label>Concurrency cap (max simultaneous calls)</Label>
              <span className="font-mono text-sm tabular-nums" data-testid="designer-cc-value">{model.concurrency_cap}</span>
            </div>
            <Slider
              min={1}
              max={100}
              step={1}
              value={[Number(model.concurrency_cap)]}
              onValueChange={(v) => setField("concurrency_cap", v[0])}
              data-testid="designer-cc-slider"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pacing">Pacing (calls per minute, optional)</Label>
            <Input
              id="pacing"
              type="number"
              min={1}
              max={600}
              value={model.pacing_per_minute}
              onChange={(e) => setField("pacing_per_minute", e.target.value)}
              placeholder="leave empty to disable throttling"
              data-testid="designer-pacing"
            />
          </div>
          <div className="rounded-md border border-border p-4 space-y-4">
            <Label>Retry rules</Label>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="ma">Max attempts</Label>
                <Input
                  id="ma"
                  type="number"
                  min={1}
                  max={10}
                  value={model.retry_rules.max_attempts}
                  onChange={(e) => setRetry("max_attempts", e.target.value)}
                  data-testid="designer-max-attempts"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="rd">Retry delay (seconds)</Label>
                <Input
                  id="rd"
                  type="number"
                  min={60}
                  max={86400}
                  value={model.retry_rules.retry_delay_seconds}
                  onChange={(e) => setRetry("retry_delay_seconds", e.target.value)}
                  data-testid="designer-retry-delay"
                />
              </div>
            </div>
            <div>
              <Label>Retry on</Label>
              <div className="mt-2 flex flex-wrap gap-3">
                {RETRY_OPTIONS.map((opt) => {
                  const checked = model.retry_rules.retry_on.includes(opt);
                  return (
                    <label key={opt} className="flex items-center gap-2 text-sm">
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(v) => {
                          const cur = new Set(model.retry_rules.retry_on);
                          if (v) cur.add(opt); else cur.delete(opt);
                          setRetry("retry_on", Array.from(cur));
                        }}
                        data-testid={`designer-retry-${opt}`}
                      />
                      {opt.replace("_", " ")}
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Sticky footer */}
      <div className="fixed bottom-0 left-60 right-0 border-t border-border bg-card/95 backdrop-blur p-3 flex items-center justify-end gap-2">
        <Button variant="outline" onClick={() => navigate("/campaigns")}>
          Cancel
        </Button>
        <Button onClick={() => save(false)} disabled={submitting} data-testid="designer-save">
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save
        </Button>
        {(!campaign || campaign.status === "draft") && (
          <Button
            onClick={() => save(true)}
            disabled={submitting || !valid}
            data-testid="designer-launch"
          >
            <Play className="h-4 w-4" /> Save & launch
          </Button>
        )}
      </div>
    </div>
  );
}
